/**
 * What the library can filter and sort on, and nothing else.
 *
 * Every option here reads a field the list endpoint actually sends. That is
 * the whole design rule, and it is why some obvious-looking filters are
 * absent:
 *
 * - There is no "Başarılı / Başarısız" project status. `Project.status` is
 *   `draft`/`active`/`archived` - an editorial state nothing in the product
 *   sets - and capture progress is per-project derived state that would cost
 *   a request each.
 * - Coverage and reconstruction filter on *existence*, not on quality.
 *   "Ölçüldü" means an analysis has been stored and "Var" means a job has been
 *   created; neither is a verdict on the project. Offering the four coverage
 *   statuses as filter values would ask the user to know what "kısmi" means
 *   before they can find anything, and would quietly turn "Bilinmiyor" - an
 *   absence of measurement - into a category of result.
 *
 * Kept as a pure module rather than component state so the rules can be
 * tested without rendering a grid.
 */

import type { ProjectListItem, SubjectType } from "@/api/types";
import { CATEGORY_OF_SUBJECT, type ProductCategory } from "@/i18n/terms";

export type CategoryFilter = ProductCategory | "all";
/** One of the six stored subject types, or every one of them. */
export type SubtypeFilter = SubjectType | "all";
/** Presence, not count: "has anything" is the question a library screen asks. */
export type PresenceFilter = "all" | "yes" | "no";
export type SortKey = "activity" | "newest" | "oldest" | "name-asc" | "name-desc";

export interface ProjectFilters {
  query: string;
  category: CategoryFilter;
  subtype: SubtypeFilter;
  media: PresenceFilter;
  coverage: PresenceFilter;
  reconstruction: PresenceFilter;
  /**
   * Projects the backend flagged as needing a decision.
   *
   * Added only because it can be answered without a request: `workflow
   * .attention` arrives on every list row and is non-empty **only** when the
   * backend measured a count or a terminal status - a failed job, files that
   * would not process, files with quality problems, unprepared videos, or a
   * head project whose measured coverage fell short. There is deliberately no
   * "aktif", "taslak" or "sağlıklı" filter next to it: those would be
   * editorial states nothing in the domain sets.
   */
  attention: boolean;
  sort: SortKey;
}

export const DEFAULT_FILTERS: ProjectFilters = {
  query: "",
  category: "all",
  subtype: "all",
  media: "all",
  coverage: "all",
  reconstruction: "all",
  attention: false,
  // Last activity, because a library is a work queue: the project you touched
  // an hour ago matters more than the one you created first.
  sort: "activity",
};

export const SORT_LABELS: Record<SortKey, string> = {
  activity: "Son etkinlik",
  newest: "En yeni",
  oldest: "En eski",
  "name-asc": "Ada göre A–Z",
  "name-desc": "Ada göre Z–A",
};

export const PRESENCE_LABELS: Record<PresenceFilter, string> = {
  all: "Tümü",
  yes: "Var",
  no: "Yok",
};

/** Coverage is measured or not measured; "var/yok" would read as a verdict. */
export const COVERAGE_PRESENCE_LABELS: Record<PresenceFilter, string> = {
  all: "Tümü",
  yes: "Ölçüldü",
  no: "Ölçülmedi",
};

/**
 * Whether anything is narrowing the list.
 *
 * Sort is deliberately excluded: reordering shows every project, so offering
 * to "clear" it would suggest results are being hidden when they are not.
 */
export function isFiltered(filters: ProjectFilters): boolean {
  return (
    filters.query.trim() !== "" ||
    filters.category !== "all" ||
    filters.subtype !== "all" ||
    filters.media !== "all" ||
    filters.coverage !== "all" ||
    filters.reconstruction !== "all" ||
    filters.attention
  );
}

/** Turkish casing, so "İSTANBUL" matches "istanbul" and not "ıstanbul". */
const fold = (value: string): string => value.toLocaleLowerCase("tr");

function matchesPresence(filter: PresenceFilter, present: boolean): boolean {
  return filter === "all" || (filter === "yes") === present;
}

function compare(a: ProjectListItem, b: ProjectListItem, sort: SortKey): number {
  switch (sort) {
    case "activity":
      return b.last_activity_at.localeCompare(a.last_activity_at);
    case "newest":
      return b.created_at.localeCompare(a.created_at);
    case "oldest":
      return a.created_at.localeCompare(b.created_at);
    case "name-asc":
      return a.name.localeCompare(b.name, "tr");
    case "name-desc":
      return b.name.localeCompare(a.name, "tr");
  }
}

export function applyFilters(
  projects: readonly ProjectListItem[],
  filters: ProjectFilters,
): ProjectListItem[] {
  const needle = fold(filters.query.trim());

  const matched = projects.filter((project) => {
    if (
      filters.category !== "all" &&
      CATEGORY_OF_SUBJECT[project.subject_type] !== filters.category
    ) {
      return false;
    }
    if (filters.subtype !== "all" && project.subject_type !== filters.subtype) {
      return false;
    }
    // Evidence-backed by construction: an empty `attention` array means the
    // backend found nothing to report, not that it did not look.
    if (filters.attention && (project.workflow?.attention.length ?? 0) === 0) {
      return false;
    }
    if (!matchesPresence(filters.media, project.media_count > 0)) return false;
    if (!matchesPresence(filters.coverage, project.latest_coverage_status !== null)) {
      return false;
    }
    if (
      !matchesPresence(
        filters.reconstruction,
        project.latest_reconstruction_status !== null,
      )
    ) {
      return false;
    }
    if (needle === "") return true;
    // Name and description only. Ids are not searched: a UUID is not something
    // anyone types, and matching one would leak the id space into the UI.
    return (
      fold(project.name).includes(needle) ||
      fold(project.description ?? "").includes(needle)
    );
  });

  // Sorted on a copy - `filter` already returned one, but making the intent
  // explicit is what stops a later refactor from sorting the fetched array in
  // place and mutating the cache under React.
  return [...matched].sort(
    (a, b) => compare(a, b, filters.sort) || a.id.localeCompare(b.id),
  );
}

/**
 * "14 projeden 5'i gösteriliyor", or just "5 proje" when nothing is hidden.
 *
 * Both halves are counted from the real arrays. A hardcoded total is the
 * failure this sentence exists to avoid: it stays plausible for exactly as
 * long as nobody has more projects than the number somebody typed.
 */
export function resultCountLabel(shown: number, total: number): string {
  if (shown === total) return `${total} proje`;
  if (shown === 0) return "Eşleşen proje yok";
  return `${total} projeden ${shown}'i gösteriliyor`;
}
